import * as D from "io-ts/Decoder"
import { UseQueryResult, useQuery } from "react-query"

import { Century } from "@/domains/centuries"
import { CountryId } from "@/domains/countries"
import { SimpleId } from "@/domains/simple-id"
import { createParams, expectJson, useHttpClient } from "@/lib/http"
import { Uuid } from "@/lib/uuid"

import { AuthorDetail, AuthorPreview } from "./model"

type GetAuthorsParams = {
  currentAuctionBlock?: boolean
  century?: Century
  country?: CountryId
}

function useAuthorsApi() {
  const client = useHttpClient()

  return {
    getAll: ({ currentAuctionBlock, ...params }: GetAuthorsParams = {}) =>
      client
        .get(`aig/authors`, {
          searchParams: createParams({
            current_auction_block: currentAuctionBlock,
            ...params,
          }),
        })
        .then(expectJson(D.array(AuthorPreview))),

    get: (id: Uuid | SimpleId) =>
      client.get(`aig/authors/${id}`).then(expectJson(AuthorDetail)),
  }
}

export function useAuthorListQuery(
  params?: GetAuthorsParams
): UseQueryResult<Array<AuthorPreview>> {
  const api = useAuthorsApi()

  return useQuery({
    queryKey: ["authors", "list", params],
    queryFn: () => api.getAll(params),
    keepPreviousData: true,
  })
}

export function useAuthorDetailQuery(
  authorId: SimpleId | Uuid | undefined
): UseQueryResult<AuthorDetail> {
  const api = useAuthorsApi()

  return useQuery({
    queryKey: ["authors", "detail", authorId],
    queryFn: () =>
      authorId === undefined ? Promise.reject() : api.get(authorId),
    enabled: authorId !== undefined,
  })
}
